/**
 * The verbs: archive, trash, junk, flag, read, move, delete.
 *
 * A layer of its own because none of these is one step. Each has to decide
 * which messages it may touch, group what is left by account, apply the change
 * locally so the row moves under the cursor, send one request, and then explain
 * an answer that is routinely partly yes and partly no. Written once here, the
 * UI is buttons; written at each button, it is six near-copies of the hard part
 * and five of them are missing the rollback.
 *
 * # Partial success is the shape of the answer, not an error case
 *
 * Archiving forty messages where one was deleted from a phone half a minute ago
 * is completely ordinary. `Email/set` says so per object, so this file reports
 * it that way: thirty-nine moved, one came back, and the one is named. The
 * alternative -- a dialog saying an error occurred -- throws away the only two
 * facts the reader needed, which are how much worked and which message did not.
 *
 * # An action whose mailbox does not exist is not offered
 *
 * Archive, Trash and Junk are mailbox ids, not protocol verbs, and plenty of
 * IMAP accounts have no Archive folder at all. So every action here answers
 * {@link eligible} first, and the UI asks the same question to decide whether
 * to draw the button enabled. A button that fails when pressed is worse than a
 * button that is visibly unavailable and says why.
 *
 * # In a unified view the accounts that can are not held back by the ones that cannot
 *
 * Selecting forty messages across five accounts and archiving them, where one
 * account has no Archive folder and another is a read-only Maildir, does the
 * thirty-odd it can and leaves the rest exactly where they were -- and names
 * the accounts in the toast. Refusing the whole batch was the alternative, and
 * it punishes the four accounts that were fine for the shortcomings of the
 * fifth; silently skipping was the other, and it leaves someone believing their
 * inbox is empty when eight messages are still in it. Doing the work and saying
 * what was left is the only version that is both useful and true.
 */

import { effect } from "@preact/signals";

import { plural } from "./format";
import {
  keywordPatch,
  movePatch,
  optimistically,
  setAcrossAccounts,
  type AccountSet,
  type EmailPatch,
  type SetError,
  type SetOutcome,
} from "./mutate";
import * as priority from "./priority";
import { autoReadSeconds } from "./settings";
import * as store from "./store";
import type { Email, Envelope } from "./types";

/** The mailbox roles an action names rather than picking by id. */
export type Role = "archive" | "trash" | "junk" | "inbox";

const ROLE_LABEL: Record<Role, string> = {
  archive: "Archive",
  trash: "Trash",
  junk: "Junk",
  inbox: "Inbox",
};

/**
 * What an action can do with a set of messages, and what it cannot.
 *
 * `reasons` is one sentence per distinct obstacle, already naming the account,
 * so it can be a tooltip on a disabled button or a clause in a toast without
 * either caller reassembling it.
 */
export interface Eligible {
  rows: Envelope[];
  reasons: string[];
  /** Messages left out. Their count is what the toast reports as "left where they were". */
  skipped: number;
}

function accountName(accountId: string): string {
  return store.accounts.value.find((a) => a.id === accountId)?.name ?? accountId;
}

function isReadOnly(accountId: string): boolean {
  return store.accounts.value.find((a) => a.id === accountId)?.isReadOnly ?? false;
}

/** Messages in accounts that accept writes at all. */
export function writable(rows: Envelope[]): Eligible {
  const ok: Envelope[] = [];
  const blocked = new Set<string>();
  for (const row of rows) {
    if (isReadOnly(row.accountId)) blocked.add(row.accountId);
    else ok.push(row);
  }
  return {
    rows: ok,
    reasons: [...blocked].map((id) => `${accountName(id)} is read-only`),
    skipped: rows.length - ok.length,
  };
}

/** Messages that can be moved to a mailbox with this role. */
export function eligible(rows: Envelope[], role: Role): Eligible {
  const first = writable(rows);
  const ok: Envelope[] = [];
  const missing = new Set<string>();
  for (const row of first.rows) {
    if (store.roleMailbox(row.accountId, role)) ok.push(row);
    else missing.add(row.accountId);
  }
  return {
    rows: ok,
    reasons: [
      ...first.reasons,
      ...[...missing].map((id) => `${accountName(id)} has no ${ROLE_LABEL[role]} folder`),
    ],
    skipped: rows.length - ok.length,
  };
}

// ---- one change to one message -------------------------------------------------

interface Change {
  /** `accountId:id`, the key the store and the rollback both use. */
  key: string;
  accountId: string;
  id: string;
  subject: string;
  /**
   * The message as it was before the change.
   *
   * Kept so that Undo can put it back into a list that no longer holds it: an
   * archived message has left every buffer, and the store cannot patch a copy
   * it does not have.
   */
  email: Email;
  /** `null` destroys the message. */
  patch: EmailPatch | null;
  /** The patch that puts it back, when the action is undoable. */
  inverse?: EmailPatch;
}

function change(row: Envelope, patch: EmailPatch | null, inverse?: EmailPatch): Change {
  return {
    key: store.keyOf(row),
    accountId: row.accountId,
    id: row.email.id,
    subject: row.email.subject || "(no subject)",
    email: row.email,
    patch,
    ...(inverse ? { inverse } : {}),
  };
}

/** The mailboxes a message is in now, other than the one it is going to. */
function sources(email: Email, to: string): string[] {
  return Object.keys(email.mailboxIds).filter((id) => email.mailboxIds[id] && id !== to);
}

/**
 * Move one message, and the patch that moves it back.
 *
 * The single-source case is {@link movePatch} and its arguments swapped, which
 * is the whole of Undo. The other case is a message filed in more than one
 * mailbox at once -- ordinary on a labelling provider -- where a move has to
 * clear every label it is leaving, and putting it back means restoring all of
 * them rather than the one that happened to be first.
 */
function moveBoth(email: Email, to: string): { patch: EmailPatch; inverse: EmailPatch } {
  const from = sources(email, to);
  if (from.length <= 1) {
    return { patch: movePatch(to, from[0]), inverse: movePatch(from[0] ?? to, to) };
  }
  const patch = movePatch(to);
  const inverse: EmailPatch = { [`mailboxIds/${to}`]: null };
  for (const id of from) {
    patch[`mailboxIds/${id}`] = null;
    inverse[`mailboxIds/${id}`] = true;
  }
  return { patch, inverse };
}

// ---- sending -------------------------------------------------------------------

/**
 * Run the batch and re-key its answer to `accountId:id`.
 *
 * The re-keying is not cosmetic. A JMAP id is unique only inside its account,
 * so two accounts in one batch can legitimately return the same id string --
 * and a rollback that matched on the bare id would restore the wrong account's
 * message while leaving the failed one archived.
 */
async function send(byAccount: Map<string, AccountSet>): Promise<SetOutcome> {
  const outcomes = await setAcrossAccounts(byAccount);
  const ok: string[] = [];
  const failed = new Map<string, SetError>();
  for (const [accountId, outcome] of outcomes) {
    for (const id of outcome.ok) ok.push(`${accountId}:${id}`);
    for (const [id, error] of outcome.failed) failed.set(`${accountId}:${id}`, error);
  }
  return { ok, failed, newState: null };
}

/** A `SetError` as a phrase that fits after a message's name. */
function explain(error: SetError | undefined): string {
  switch (error?.type) {
    case "notFound":
      return "no longer on the server";
    case "forbidden":
      return "not allowed on that account";
    case "invalidProperties":
      return "not a change the server accepts";
    case "overQuota":
      return "over the account's quota";
    case "tooLarge":
      return "too large for the target mailbox";
    case "rateLimit":
      return "refused for now — too many changes at once";
    default:
      return error?.description || error?.type || "refused without a reason";
  }
}

/**
 * Why the failures failed, named where naming them is short enough to read.
 *
 * Three or fewer and the messages are listed by subject, because "which one"
 * is the question someone will otherwise have to answer by scrolling. More than
 * that and the subjects are noise, so the causes are counted instead.
 */
function describe(changes: Change[], failed: Map<string, SetError>): string {
  const items = changes.filter((c) => failed.has(c.key));
  if (items.length <= 3) {
    return items.map((c) => `“${c.subject}” — ${explain(failed.get(c.key))}`).join("; ");
  }
  const counts = new Map<string, number>();
  for (const c of items) {
    const why = explain(failed.get(c.key));
    counts.set(why, (counts.get(why) ?? 0) + 1);
  }
  return [...counts].map(([why, n]) => `${n} ${why}`).join("; ");
}

interface Verb {
  /** "Archived", "Moved to Trash", "Marked as read". */
  past: string;
  /** The same thing after "could not be": "archived", "moved to Trash". */
  failedPast?: string;
  /** Offer Undo, which resends the batch with every patch inverted. */
  undoable?: boolean;
  /** Say so even when it is one message. Off for toggles that are visible anyway. */
  chatty?: boolean;
  /** Clauses about messages that were never sent, e.g. a missing Archive folder. */
  left?: string[];
  /** Drop the selection afterwards. True where the rows have left the view. */
  deselect?: boolean;
}

async function commit(changes: Change[], verb: Verb): Promise<void> {
  if (changes.length === 0) {
    if (verb.left?.length) store.toast(`Nothing to do — ${verb.left.join("; ")}.`, "error");
    return;
  }

  const byAccount = new Map<string, AccountSet>();
  for (const c of changes) {
    const set = byAccount.get(c.accountId) ?? {};
    if (c.patch === null) set.destroy = [...(set.destroy ?? []), c.id];
    else set.update = { ...(set.update ?? {}), [c.id]: c.patch };
    byAccount.set(c.accountId, set);
  }

  const edits = new Map<string, EmailPatch | null>(changes.map((c) => [c.key, c.patch]));
  const bases = new Map<string, Email>(changes.map((c) => [c.key, c.email]));
  const rollback: { undo: ((keys?: string[]) => void) | null } = { undo: null };

  let outcome: SetOutcome;
  try {
    outcome = await optimistically(
      () => {
        rollback.undo = store.editLocally(edits, bases);
      },
      () => send(byAccount),
      (failedKeys) => rollback.undo?.(failedKeys.length > 0 ? failedKeys : undefined),
    );
  } catch (e) {
    store.toast(e instanceof Error ? e.message : String(e), "error");
    return;
  }

  const done = changes.filter((c) => !outcome.failed.has(c.key));
  const suffix = verb.left?.length ? ` (${verb.left.join("; ")})` : "";

  if (done.length > 0 && (verb.chatty !== false || done.length > 1 || suffix)) {
    const undo =
      verb.undoable && done.some((c) => c.inverse)
        ? {
            label: "Undo",
            run: () => void undoAll(done),
          }
        : undefined;
    store.toast(`${verb.past} ${plural(done.length, "message")}${suffix}`, "info", undo);
  }

  if (outcome.failed.size > 0) {
    const n = outcome.failed.size;
    const what = verb.failedPast ?? verb.past.toLowerCase();
    store.toast(
      `${n} of ${changes.length} could not be ${what}: ${describe(changes, outcome.failed)}`,
      "error",
    );
  }

  if (verb.deselect && done.length > 0) store.clearSelection();
}

/** Resend the batch inverted. Undo is the same machinery, pointed backwards. */
async function undoAll(done: Change[]): Promise<void> {
  const back = done
    .filter((c): c is Change & { inverse: EmailPatch } => Boolean(c.inverse))
    .map((c) => ({ ...c, patch: c.inverse, inverse: c.patch ?? undefined }));
  await commit(back, { past: "Put back", chatty: true });
}

// ---- the verbs -----------------------------------------------------------------

/**
 * Mark read or unread.
 *
 * Only the messages that would actually change are sent. On a forty-row
 * selection most are already read, and a patch that sets `$seen` on a message
 * that has it is a provider write, an outbox op and an upstream round trip for
 * no change at all.
 */
export async function markSeen(rows: Envelope[], seen: boolean): Promise<void> {
  // Both of the things below are about the message on screen, so both are
  // asked the same question first: is the open message one of the ones this
  // call is about? Cancelling unconditionally was the earlier version and it
  // was wrong in a quiet way -- Shift+U over a selection that did *not* include
  // what was open still killed that message's pending timer, so a message
  // being read at the time was never marked read at all. Found by the test in
  // `actions.test.ts` that says so.
  const open = store.openEmailId.peek();
  const touchesOpen = Boolean(open) && rows.some((r) => store.keyOf(r) === open);

  // An explicit choice outranks the pending automatic one. Without this,
  // pressing Shift+U on the message you are reading marks it unread and then
  // the timer started when it opened marks it read again a second later --
  // which reads as the app arguing with you.
  if (touchesOpen) cancelAutoRead();
  // Cancelling was not enough on its own, and this is the whole of the bug it
  // failed to fix: the effect below depends on the open message's keywords, so
  // clearing `$seen` re-runs it, it sees an unread message on screen, and it
  // arms a *new* timer. The message flipped straight back to read. Suppressing
  // the message rather than the timer is what makes the reader's decision
  // stick, because it survives the re-run that the decision itself causes.
  if (!seen && touchesOpen) autoReadSuppressed = open;

  const can = writable(rows);
  const changes = can.rows
    .filter((r) => Boolean(r.email.keywords["$seen"]) !== seen)
    .map((r) => change(r, keywordPatch("$seen", seen), keywordPatch("$seen", !seen)));
  await commit(changes, {
    past: seen ? "Marked as read" : "Marked as unread",
    chatty: false,
    left: can.reasons,
  });
}

// ---- reading a message marks it read ------------------------------------------

let autoReadTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * The open message the reader has deliberately marked unread.
 *
 * Not a signal: nothing renders from it, and making it one would feed the very
 * effect it exists to hold back. Keyed by message rather than a bare flag so it
 * cannot leak onto the next thing opened -- and cleared as soon as a different
 * message is open, because coming back to a message later is a fresh open and
 * should be timed like one. "Unread" here means "leave it alone while I am
 * looking at it", not "never mark this read again".
 */
let autoReadSuppressed: string | null = null;

function cancelAutoRead(): void {
  clearTimeout(autoReadTimer);
  autoReadTimer = undefined;
}

/**
 * Mark the open message read once it has been open long enough.
 *
 * A signal effect rather than a `useEffect` in `Reader`, for two reasons.
 * `Reader` returns early when nothing is open, so a hook after that point would
 * break hook order; and this is a property of *the open message*, not of a
 * component being mounted -- a reading pane that stays mounted across a
 * selection change would otherwise have to re-derive it anyway.
 *
 * Re-checked when the timer fires rather than trusted from when it was set: in
 * a second and a half the message can be closed, replaced by `j`, archived out
 * of the list, or marked read by another device through the event stream, and
 * acting on a stale row would write to whatever is open *now*.
 */
effect(() => {
  const row = store.openEmail.value;
  const key = store.openEmailId.value;
  // Read inside the effect so it is a dependency: changing the setting while a
  // message is open re-arms the timer at the new delay rather than leaving the
  // old one to fire, which is the difference between a setting and a setting
  // that appears not to work until you click something else.
  const seconds = autoReadSeconds.value;

  cancelAutoRead();
  // A different message is open, so the previous reader's decision no longer
  // applies to anything. Cleared here rather than in `open()` so the two facts
  // that must agree -- which message is open, and which one is suppressed --
  // are read in the same place.
  if (key !== autoReadSuppressed) autoReadSuppressed = null;
  if (seconds === null) return;
  if (!row || !key) return;
  if (row.email.keywords["$seen"]) return;
  if (key === autoReadSuppressed) return;
  // Guarded here rather than left to `markSeen`, which would report "nothing to
  // do -- X is read-only" as a toast. True of every message in that account, so
  // it would fire on every open.
  if (writable([row]).rows.length === 0) return;

  autoReadTimer = setTimeout(() => {
    autoReadTimer = undefined;
    const still = store.openEmail.peek();
    if (!still || store.openEmailId.peek() !== key) return;
    if (still.email.keywords["$seen"]) return;
    void markSeen([still], true);
  }, Math.max(0, seconds) * 1000);
});

/** Flag or unflag. The inverse of a toggle is the toggle, so there is no Undo. */
export async function markFlagged(rows: Envelope[], flagged: boolean): Promise<void> {
  const can = writable(rows);
  const changes = can.rows
    .filter((r) => Boolean(r.email.keywords["$flagged"]) !== flagged)
    .map((r) => change(r, keywordPatch("$flagged", flagged), keywordPatch("$flagged", !flagged)));
  await commit(changes, {
    past: flagged ? "Flagged" : "Unflagged",
    chatty: false,
    left: can.reasons,
  });
}

/**
 * Move to the mailbox with a given role, per account.
 *
 * The role is resolved separately in each account, which is what makes one
 * click work across a unified view: "Archive" means account 1's Archive folder
 * for account 1's messages and account 2's for account 2's, and neither has to
 * be the same mailbox name.
 */
export async function moveToRole(rows: Envelope[], role: Role): Promise<void> {
  const can = eligible(rows, role);
  const changes: Change[] = [];
  for (const row of can.rows) {
    const to = store.roleMailbox(row.accountId, role);
    if (!to) continue;
    const { patch, inverse } = moveBoth(row.email, to);
    changes.push(change(row, patch, inverse));
  }
  const past =
    role === "archive"
      ? "Archived"
      : role === "trash"
        ? "Moved to Trash"
        : role === "junk"
          ? "Marked as junk"
          : "Moved to Inbox";
  await commit(changes, {
    past,
    failedPast: role === "archive" ? "archived" : `moved to ${ROLE_LABEL[role]}`,
    undoable: true,
    left: can.reasons,
    deselect: true,
  });
}

/**
 * Move to one named mailbox.
 *
 * Single-account only, and the UI enforces that by hiding the menu when the
 * selection spans accounts. A mailbox id belongs to one account, and the
 * alternative -- matching folders across accounts by name -- guesses that
 * "Receipts" in one account means the same thing as "Receipts" in another,
 * which is a guess a mail client has no business making silently.
 */
export async function moveToMailbox(rows: Envelope[], mailboxId: string): Promise<void> {
  const can = writable(rows);
  const changes = can.rows
    .filter((r) => !r.email.mailboxIds[mailboxId])
    .map((r) => {
      const { patch, inverse } = moveBoth(r.email, mailboxId);
      return change(r, patch, inverse);
    });
  await commit(changes, { past: "Moved", undoable: true, left: can.reasons, deselect: true });
}

/**
 * Delete for good.
 *
 * Distinct from Trash, which is {@link moveToRole}, and not undoable: this asks
 * the provider to unlink the message, and no inverse patch can bring it back.
 * The confirmation the UI puts in front of it has to say that.
 */
export async function deleteForever(rows: Envelope[]): Promise<void> {
  const can = writable(rows);
  const changes = can.rows.map((r) => change(r, null));
  await commit(changes, { past: "Deleted", left: can.reasons, deselect: true });
}

// ---- the low-priority inbox --------------------------------------------------

/*
 * These three are unlike everything above them, and the difference is worth
 * naming at the top rather than discovering halfway down.
 *
 * Every other verb in this file is an `Email/set` about the messages in hand:
 * it patches a local copy, sends one request, and rolls the copy back if the
 * server disagrees. None of that applies here. A sender rule is not a change to
 * a message at all -- it is a change to a *rule*, which the daemon then applies
 * retroactively to every message that sender has ever sent, across mailboxes
 * this client has not paged into and accounts it is not currently showing.
 *
 * So there is no optimistic patch and no per-message undo: there is nothing
 * this side could patch that would be right. The rule is written, and then the
 * whole view is asked for again. It is slower than the other verbs by a round
 * trip, and it is the only version that does not lie about what happened.
 *
 * And nothing here reaches the provider. That is the constraint the whole
 * feature is built around -- see `hm_store::priority` on the daemon. A demoted
 * sender's mail is in exactly the folders it always was, with exactly the flags
 * it always had, on every other client the reader owns.
 */

/** The one sender a rule could be about, if the selection has exactly one. */
export function soleSender(rows: Envelope[]): string | undefined {
  const addresses = new Set(
    rows.map((r) => priority.senderOf(r.email)).filter((a): a is string => Boolean(a)),
  );
  return addresses.size === 1 ? [...addresses][0] : undefined;
}

/**
 * Send a sender to the low-priority inbox, retroactively.
 *
 * The toast reports how much moved, because that number is the feature
 * explaining itself: "212 messages" is how someone learns this reaches
 * backwards through the archive, and no sentence in a settings page teaches it
 * as well. Undo removes the rule again, which restores every one of them --
 * `hm_store::priority::remove` re-tests each message against the rules that
 * survive, so undoing one rule cannot free mail that a second rule still
 * claims.
 */
export async function demoteSender(
  address: string,
  scope: priority.RuleScope = "address",
): Promise<void> {
  let result;
  try {
    result = await priority.create(address, scope);
  } catch (e) {
    store.toast(e instanceof Error ? e.message : String(e), "error");
    return;
  }
  await store.reclassified();

  const what = scope === "domain" ? `Everyone at ${address}` : address;
  const moved = result.reclassified;
  const rule = result.rule;
  store.toast(
    moved > 0
      ? `${what} is low priority — ${plural(moved, "message")} moved`
      : `${what} is low priority`,
    "info",
    rule
      ? {
          label: "Undo",
          run: () => {
            void (async () => {
              try {
                await priority.remove(rule.id);
              } catch (e) {
                store.toast(e instanceof Error ? e.message : String(e), "error");
                return;
              }
              await store.reclassified();
            })();
          },
        }
      : undefined,
  );
}

/**
 * Put a sender back in the focused inbox.
 *
 * Reads the rule list rather than being told which rule to delete, because the
 * client genuinely does not know: a sender can be caught by a rule on their
 * address, by a rule on their whole domain, or by both at once, and the message
 * row carries only the verdict. Every rule that claims this address goes, which
 * is the only reading of "put this sender back" that actually puts them back.
 *
 * The cost is a round trip to list the rules before the one that deletes them.
 * That is acceptable on a deliberate, rare action, and it is a great deal
 * better than a menu item that appears to work and leaves the domain rule in
 * place.
 */
export async function restoreSender(address: string): Promise<void> {
  const domain = priority.domainOf(address.toLowerCase());
  let removed = 0;
  try {
    const rules = await priority.list();
    const claiming = rules.filter((r) =>
      r.scope === "domain"
        ? Boolean(domain) && r.pattern === domain
        : // Compared against the normalised `pattern` rather than the display
          // spelling: a Gmail address written with dots is the same mailbox,
          // and the daemon already folded it.
          r.pattern === address.toLowerCase() || r.display.toLowerCase() === address.toLowerCase(),
    );
    if (claiming.length === 0) {
      store.toast("No rule to remove — this sender is not on the low-priority list.", "error");
      return;
    }
    for (const rule of claiming) {
      const result = await priority.remove(rule.id);
      removed += result.reclassified;
    }
  } catch (e) {
    store.toast(e instanceof Error ? e.message : String(e), "error");
    return;
  }
  await store.reclassified();
  store.toast(
    removed > 0
      ? `${address} is back in Focused — ${plural(removed, "message")} moved`
      : `${address} is back in Focused`,
  );
}
